import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from './components/ui/sonner';
import { Navbar } from './components/Navbar';
import { ChatSidebar } from './components/ChatSidebar';
import CookieBanner from './components/CookieBanner';

import ErrorBoundary from './components/ErrorBoundary';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import CompleteRegistrationPage from './pages/CompleteRegistrationPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ProfilePage from './pages/ProfilePage';
import FeedPage from './pages/FeedPage';
import SearchPage from './pages/SearchPage';
import MessagesPage from './pages/MessagesPage';
import ChatPage from './pages/ChatPage';
import ChatsListPage from './pages/ChatsListPage';
import SubscriptionPage from './pages/SubscriptionPage';
import ValidationPage from './pages/ValidationPage';
import MemberProfilePage from './pages/MemberProfilePage';
import AdminPage from './pages/AdminPage';
import VisitsPage from './pages/VisitsPage';
import NotificationsPage from './pages/NotificationsPage';
import CGUPage from './pages/CGUPage';
import FollowPage from './pages/FollowPage';
import SettingsPage from './pages/SettingsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import ContactPage from './pages/ContactPage';
import PendingValidationPage from './pages/PendingValidationPage';

import './App.css';

// Protected Route component
const ProtectedRoute = ({ children, requireComplete = true, requireValidated = true }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Redirect users with incomplete profile to complete registration
  // Skip for admin users
  if (requireComplete && !user.profile_complete && !user.is_admin) {
    return <Navigate to="/complete-registration" replace />;
  }
  
  // Redirect non-validated users to pending validation page
  // Skip for admin users
  if (requireValidated && user.profile_complete && !user.is_validated && !user.is_admin) {
    return <Navigate to="/pending-validation" replace />;
  }
  
  return children;
};

// Public Route - redirects to feed if already logged in
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }
  
  if (user) {
    // Non-validated users go to pending page
    if (user.profile_complete && !user.is_validated && !user.is_admin) {
      return <Navigate to="/pending-validation" replace />;
    }
    return <Navigate to="/feed" replace />;
  }
  
  return children;
};

// Layout with Navbar and Chat Sidebar
const AppLayout = ({ children, showNavbar = true, showChat = false }) => {
  const { user } = useAuth();
  const [chatOpen, setChatOpen] = useState(true);
  
  // Only show chat sidebar for authenticated users with complete profiles
  const canShowChat = showChat && user && (user.profile_complete || user.is_admin);
  
  return (
    <>
      {showNavbar && <Navbar />}
      {canShowChat && (
        <ChatSidebar 
          isOpen={chatOpen} 
          onClose={() => setChatOpen(false)}
          onToggle={() => setChatOpen(!chatOpen)}
        />
      )}
      <div className={canShowChat && chatOpen ? 'md:ml-72 transition-all duration-300' : 'transition-all duration-300'}>
        {children}
      </div>
    </>
  );
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route 
        path="/" 
        element={
          <PublicRoute>
            <AppLayout showNavbar={false}>
              <LandingPage />
            </AppLayout>
          </PublicRoute>
        } 
      />
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <AppLayout showNavbar={false}>
              <LoginPage />
            </AppLayout>
          </PublicRoute>
        } 
      />
      <Route 
        path="/register" 
        element={
          <PublicRoute>
            <AppLayout showNavbar={false}>
              <RegisterPage />
            </AppLayout>
          </PublicRoute>
        } 
      />
      <Route 
        path="/forgot-password" 
        element={
          <PublicRoute>
            <AppLayout showNavbar={false}>
              <ForgotPasswordPage />
            </AppLayout>
          </PublicRoute>
        } 
      />
      <Route 
        path="/reset-password" 
        element={
          <PublicRoute>
            <AppLayout showNavbar={false}>
              <ResetPasswordPage />
            </AppLayout>
          </PublicRoute>
        } 
      />
      <Route 
        path="/cgu" 
        element={
          <AppLayout showNavbar={true}>
            <CGUPage />
          </AppLayout>
        } 
      />
      <Route 
        path="/privacy" 
        element={
          <AppLayout showNavbar={true}>
            <PrivacyPolicyPage />
          </AppLayout>
        } 
      />
      <Route 
        path="/contact" 
        element={
          <AppLayout showNavbar={true}>
            <ContactPage />
          </AppLayout>
        } 
      />

      {/* Protected routes */}
      <Route 
        path="/pending-validation" 
        element={
          <ProtectedRoute requireComplete={true} requireValidated={false}>
            <PendingValidationPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/complete-registration" 
        element={
          <ProtectedRoute requireComplete={false} requireValidated={false}>
            <AppLayout showNavbar={false}>
              <CompleteRegistrationPage />
            </AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/feed" 
        element={
          <ProtectedRoute>
            <AppLayout showChat={true}>
              <FeedPage />
            </AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/profile" 
        element={
          <ProtectedRoute>
            <AppLayout showChat={true}>
              <ProfilePage />
            </AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/search" 
        element={
          <ProtectedRoute>
            <AppLayout showChat={true}>
              <SearchPage />
            </AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/messages" 
        element={
          <ProtectedRoute>
            <AppLayout showChat={true}>
              <MessagesPage />
            </AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/chat" 
        element={
          <ProtectedRoute>
            <AppLayout showChat={false}>
              <ChatsListPage />
            </AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/chat/:userId" 
        element={
          <ProtectedRoute>
            <AppLayout showChat={true}>
              <ChatPage />
            </AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/subscription" 
        element={
          <ProtectedRoute>
            <AppLayout showChat={true}>
              <SubscriptionPage />
            </AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/subscription/success" 
        element={
          <ProtectedRoute>
            <AppLayout showChat={true}>
              <SubscriptionPage />
            </AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/validation" 
        element={
          <ProtectedRoute>
            <AppLayout showChat={true}>
              <ValidationPage />
            </AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/member/:userId" 
        element={
          <ProtectedRoute>
            <AppLayout showChat={true}>
              <MemberProfilePage />
            </AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/relations" 
        element={
          <ProtectedRoute>
            <AppLayout showChat={true}>
              <FollowPage />
            </AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute>
            <AppLayout showChat={true}>
              <AdminPage />
            </AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute>
            <AppLayout showChat={true}>
              <SettingsPage />
            </AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/visits" 
        element={
          <ProtectedRoute>
            <AppLayout showChat={true}>
              <VisitsPage />
            </AppLayout>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/notifications" 
        element={
          <ProtectedRoute>
            <AppLayout showChat={true}>
              <NotificationsPage />
            </AppLayout>
          </ProtectedRoute>
        } 
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ThemeProvider>
          <BrowserRouter>
            <WebSocketProvider>
              <AppRoutes />
            </WebSocketProvider>
            <Toaster 
              position="top-right"
              toastOptions={{
                style: {
                  background: 'var(--bg-paper)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-default)',
                },
              }}
            />
            <CookieBanner />
          </BrowserRouter>
        </ThemeProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
